/**
 * @fileoverview Formulário de Premissas de CAPEX e NWC
 * 
 * @module CapexNWCForm
 * @description
 * Permite editar premissas de CAPEX e NWC (Working Capital) para ambas empresas.
 * Valores são inseridos como % (da receita ou da variação de receita).
 * 
 * @features
 * - CAPEX e NWC editáveis por empresa e por ano (2025-2029)
 * - Entrada como percentual (0-100%)
 * - Validação de ranges realistas
 * - Tooltips explicativos
 * - Visualização lado a lado (Sétima vs 555)
 */

import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Alert,
  ToggleButtonGroup,
  ToggleButton,
  Divider,
  Tooltip,
} from '@mui/material';
import { 
  Info,
  AttachMoney,
  Business,
  Movie,
  Autorenew,
  TrendingUp,
  Warning,
  Lightbulb,
} from '@mui/icons-material';

import { PercentageInput } from './CommonInputs';
import { validateCapex } from '../../hooks/useValuationValidations';
import { GlossarioTooltip } from '../glossario/GlossarioTooltip';
import { useGlossarioModal } from '../../hooks/useGlossarioModal';

/**
 * Interface para premissas de CAPEX e NWC de uma empresa
 */
export interface CapexNWCData {
  // CAPEX como % da receita
  capex: {
    [ano: number]: number; // 2025-2029
  };
  
  // NWC (Working Capital) como % da variação de receita
  nwc: {
    [ano: number]: number; // 2025-2029
  };
}

/**
 * Valores padrão para Sétima (Asset-Light Model)
 */
const DEFAULTS_SETIMA: CapexNWCData = {
  capex: {
    2025: 0.05, // 5% - Investimento moderado
    2026: 0.05,
    2027: 0.05,
    2028: 0.05,
    2029: 0.05,
  },
  nwc: {
    2025: 0.035, // 3.5% - Gestão eficiente de capital de giro
    2026: 0.035,
    2027: 0.035,
    2028: 0.035,
    2029: 0.035,
  },
};

/**
 * Valores padrão para 555 Studios (Tech Growth Model)
 */
const DEFAULTS_555: CapexNWCData = {
  capex: {
    2025: 0.20, // 20% - Alto investimento em tech
    2026: 0.18, // 18% - Redução gradual com escala
    2027: 0.16,
    2028: 0.15,
    2029: 0.15,
  },
  nwc: {
    2025: 0.06, // 6% - Capital de giro para crescimento
    2026: 0.06,
    2027: 0.05, // 5% - Melhoria com escala
    2028: 0.05,
    2029: 0.05,
  },
};

interface CapexNWCFormProps {
  /** Dados da Sétima */
  setimaData?: CapexNWCData;
  /** Dados do 555 Studios */
  studios555Data?: CapexNWCData;
  /** Callback quando dados da Sétima mudam */
  onSetimaChange?: (data: CapexNWCData) => void;
  /** Callback quando dados do 555 mudam */
  onStudio555Change?: (data: CapexNWCData) => void;
  /** 🆕 Ano inicial das projeções (default: 2025) */
  firstProjectionYear?: number;
}

export default function CapexNWCForm({
  setimaData = DEFAULTS_SETIMA,
  studios555Data = DEFAULTS_555,
  onSetimaChange,
  onStudio555Change,
  firstProjectionYear = 2025, // 🆕 Default para backward compatibility
}: CapexNWCFormProps) {
  const [activeCompany, setActiveCompany] = useState<'setima' | '555'>('setima');
  const { abrir: abrirGlossario } = useGlossarioModal();
  
  const [localSetima, setLocalSetima] = useState(setimaData);
  const [local555, setLocal555] = useState(studios555Data);
  
  // Validações de CAPEX (armazena validações por empresa e ano)
  const [capexValidations, setCapexValidations] = useState<Record<string, ReturnType<typeof validateCapex>>>({});

  // 🆕 Calcular anos dinâmicos baseado em firstProjectionYear
  const projectionYears = [
    firstProjectionYear,
    firstProjectionYear + 1,
    firstProjectionYear + 2,
    firstProjectionYear + 3,
    firstProjectionYear + 4,
  ];

  // Sincronizar props com state local
  useEffect(() => {
    setLocalSetima(setimaData);
  }, [setimaData]);

  useEffect(() => {
    setLocal555(studios555Data);
  }, [studios555Data]);

  // Handlers para CAPEX
  const handleCapexChange = (
    company: 'setima' | '555',
    year: number,
    value: number
  ) => {
    // Validar CAPEX
    const validation = validateCapex(value, year);
    const validationKey = `${company}-${year}`;
    setCapexValidations(prev => ({
      ...prev,
      [validationKey]: validation,
    }));
    
    if (company === 'setima') {
      const updated = {
        ...localSetima,
        capex: { ...localSetima.capex, [year]: value }, // ✅ SEM divisão - PercentageInput já converte
      };
      setLocalSetima(updated);
      onSetimaChange?.(updated);
    } else {
      const updated = {
        ...local555,
        capex: { ...local555.capex, [year]: value }, // ✅ SEM divisão
      };
      setLocal555(updated);
      onStudio555Change?.(updated);
    }
  };

  // Handlers para NWC
  const handleNWCChange = (
    company: 'setima' | '555',
    year: number,
    value: number
  ) => {
    if (company === 'setima') {
      const updated = {
        ...localSetima,
        nwc: { ...localSetima.nwc, [year]: value }, // ✅ SEM divisão
      };
      setLocalSetima(updated);
      onSetimaChange?.(updated);
    } else {
      const updated = {
        ...local555,
        nwc: { ...local555.nwc, [year]: value }, // ✅ SEM divisão
      };
      setLocal555(updated);
      onStudio555Change?.(updated);
    }
  };

  const currentData = activeCompany === 'setima' ? localSetima : local555;

  return (
    <Paper elevation={2} sx={{ p: 3, mt: 3 }}>
      {/* Cabeçalho */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <AttachMoney sx={{ fontSize: 28 }} />
        <Typography variant="h6" fontWeight="bold">
          CAPEX e Capital de Giro (NWC)
        </Typography>
        <Tooltip 
          title="CAPEX: Investimentos em ativos fixos (% da receita). NWC: Capital de giro necessário (% da variação de receita)"
          arrow
        >
          <Info sx={{ fontSize: 18, color: 'text.secondary', cursor: 'help' }} />
        </Tooltip>
      </Box>

      {/* Seletor de Empresa */}
      <Box sx={{ mb: 3 }}>
        <ToggleButtonGroup
          value={activeCompany}
          exclusive
          onChange={(_, value) => value && setActiveCompany(value)}
          fullWidth
        >
          <ToggleButton value="setima">
            <Business sx={{ mr: 1, fontSize: 18 }} /> Sétima
          </ToggleButton>
          <ToggleButton value="555">
            <Movie sx={{ mr: 1, fontSize: 18 }} /> 555 Studios
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      <Divider sx={{ mb: 3 }} />

      {/* Descrição do modelo */}
      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2">
          {activeCompany === 'setima' ? (
            <>
              <strong>Sétima:</strong> Modelo asset-light com CAPEX moderado (5%) e gestão eficiente de capital de giro (3,5%).
            </>
          ) : (
            <>
              <strong>555 Studios:</strong> Tech growth com alto investimento inicial (20% CAPEX) reduzindo com escala, e capital de giro para suportar crescimento (5-6%).
            </>
          )}
        </Typography>
      </Alert>

      {/* Seção CAPEX */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <TrendingUp sx={{ fontSize: 20 }} />
          <Typography variant="subtitle1" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            CAPEX (% da Receita)
            <GlossarioTooltip
              termo="FCF"
              definicao="Capital Expenditure - Investimentos em ativos fixos (equipamentos, tecnologia, expansão)"
              onClick={() => abrirGlossario('FCF')}
            />
          </Typography>
        </Box>

        <Grid container spacing={2}>
          {projectionYears.map((year) => {
            const validationKey = `${activeCompany}-${year}`;
            const validation = capexValidations[validationKey];
            
            return (
              <Grid item xs={12} sm={6} md={2.4} key={`capex-${year}`}>
                <PercentageInput
                  label={`${year}`}
                  value={currentData.capex[year] || 0}
                  onChange={(value) => handleCapexChange(activeCompany, year, value)}
                  min={0}
                  max={50}
                  step={1}
                  asDecimal={true}
                />
                {/* Validação de CAPEX */}
                {validation && !validation.isValid && validation.error && (
                  <Alert severity="error" sx={{ mt: 1, fontSize: '0.75rem' }}>
                    {validation.error}
                  </Alert>
                )}
                {validation && validation.isValid && validation.warning && (
                  <Alert severity={validation.severity === 'info' ? 'info' : 'warning'} sx={{ mt: 1, fontSize: '0.75rem' }}>
                    {validation.warning}
                  </Alert>
                )}
              </Grid>
            );
          })}
        </Grid>
      </Box>

      <Divider sx={{ mb: 3 }} />

      {/* Seção NWC (Working Capital) */}
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <Autorenew sx={{ fontSize: 20 }} />
          <Typography variant="subtitle1" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            NWC - Capital de Giro (% da Δ Receita)
            <GlossarioTooltip
              termo="FCF"
              definicao="Net Working Capital - Capital de giro necessário para operações (contas a receber + estoque - contas a pagar)"
              onClick={() => abrirGlossario('FCF')}
            />
          </Typography>
          <Tooltip 
            title="Capital de giro necessário para suportar o crescimento, calculado como % da variação de receita"
            arrow
          >
            <Info sx={{ fontSize: 16, color: 'text.secondary', cursor: 'help' }} />
          </Tooltip>
        </Box>

        <Grid container spacing={2}>
          {projectionYears.map((year) => {
            return (
              <Grid item xs={12} sm={6} md={2.4} key={`nwc-${year}`}>
                <PercentageInput
                  label={`${year}`}
                  value={currentData.nwc[year] || 0}
                  onChange={(value) => handleNWCChange(activeCompany, year, value)}
                  min={0}
                  max={20}
                  step={1}
                  asDecimal={true}
                />
              </Grid>
            );
          })}
        </Grid>

        {/* Validações NWC */}
        {Object.values(currentData.nwc).some(v => v > 0.15) && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            <Warning sx={{ fontSize: 16, verticalAlign: 'middle', mr: 0.5 }} />
            NWC acima de 15% é incomum. Verifique se a empresa tem ciclo de caixa muito longo.
          </Alert>
        )}
      </Box>

      {/* Dica final */}
      <Alert severity="success" sx={{ mt: 3 }}>
        <Typography variant="body2">
          <Lightbulb sx={{ fontSize: 16, verticalAlign: 'middle', mr: 0.5 }} />
          <strong>Dica:</strong> CAPEX alto indica crescimento via investimentos. NWC alto indica necessidade de capital para financiar operações durante crescimento.
        </Typography>
      </Alert>
    </Paper>
  );
}
